import type { ItemState } from "@features/session/hooks/types";
import type { BaseItemSchema } from "@memorang/types/src/session/new-session";
import type {
	ProethicaStatTile,
	ProethicaStatValue,
} from "./proethica-report-stats";

export type ProethicaTestScore = {
	numCorrect: number;
	numTotal: number;
	percentage: number;
};

// Post-test pass gate: scoring >= 80% unlocks review and persists the pass.
export const PROETHICA_PASS_PERCENTAGE = 80;

export const isProethicaTestPassed = (percentage: number): boolean =>
	percentage >= PROETHICA_PASS_PERCENTAGE;

export type ProethicaTestReportCopy = {
	title: string;
	description: string;
	scoreTitle: string;
};

export const buildProethicaTestReportCopy = (
	isPretest: boolean,
	passed: boolean,
): ProethicaTestReportCopy => {
	if (isPretest) {
		return {
			title: "Here's your starting point",
			description:
				"This is your baseline. You'll see your progress when you take the post-test.",
			scoreTitle: "Baseline score",
		};
	}
	if (passed) {
		return {
			title: "You passed",
			description: `You're past the ${PROETHICA_PASS_PERCENTAGE}% pass mark. You can now review every answer and its feedback.`,
			scoreTitle: "Your score",
		};
	}
	return {
		title: "Just short this time",
		description: `You need ${PROETHICA_PASS_PERCENTAGE}% to pass. Go back through the modules and retake the post-test.`,
		scoreTitle: "Your score",
	};
};

// The footer's contained primary action differs per report variant. Continue is
// never a footer primary: on the pre-test it lives in the next-entry card, so
// "none" means the footer leads with an outlined Go home while the card owns the
// primary. Review is exclusive to the passed post-test - the pre-test can never
// surface it, keeping the shared question pool hidden before the post-test.
export type ProethicaTestReportFooterPrimary =
	| "none"
	| "review"
	| "retake"
	| "goHome";

export type ProethicaTestReportComposition = {
	showNextEntryCard: boolean;
	footerPrimary: ProethicaTestReportFooterPrimary;
	goHomePrimary: boolean;
};

export const resolveProethicaTestReportComposition = ({
	isPretest,
	passed,
	nextEntryIsHome,
}: {
	isPretest: boolean;
	passed: boolean;
	nextEntryIsHome: boolean;
}): ProethicaTestReportComposition => {
	if (isPretest) {
		return {
			showNextEntryCard: !nextEntryIsHome,
			footerPrimary: nextEntryIsHome ? "goHome" : "none",
			goHomePrimary: nextEntryIsHome,
		};
	}
	return {
		showNextEntryCard: false,
		footerPrimary: passed ? "review" : "retake",
		goHomePrimary: false,
	};
};

export type ProethicaScoreDeltaDirection = "up" | "down" | "flat";

export type ProethicaScoreDelta = {
	deltaPts: number;
	direction: ProethicaScoreDeltaDirection;
	label: string;
};

export const buildProethicaScoreDelta = (
	prePercentage: number,
	postPercentage: number,
): ProethicaScoreDelta => {
	const deltaPts = postPercentage - prePercentage;
	const direction: ProethicaScoreDeltaDirection =
		deltaPts > 0 ? "up" : deltaPts < 0 ? "down" : "flat";
	const label =
		direction === "flat"
			? "Same score as your pre-test"
			: `You're ${direction} ${Math.abs(deltaPts)} points`;

	return { deltaPts, direction, label };
};

const isItemCorrect = (
	item: BaseItemSchema,
	selectedChoiceIds: string[],
): boolean => {
	const correctIds = (item.content?.choices ?? [])
		.filter((choice) => choice.isCorrect)
		.map((choice) => choice.id);

	if (!(correctIds.length && selectedChoiceIds.length)) {
		return false;
	}

	return (
		correctIds.length === selectedChoiceIds.length &&
		correctIds.every((id) => selectedChoiceIds.includes(id))
	);
};

// Pre/post tests are scored from local session state: each item's selection in
// `itemStates` is matched against the choices flagged correct. Unanswered items
// count as incorrect, so the total always reflects every question.
export const computeProethicaTestScore = (
	sessionItems: BaseItemSchema[],
	itemStates?: Map<number, ItemState>,
): ProethicaTestScore => {
	const numTotal = sessionItems.length;
	const numCorrect = sessionItems.reduce((count, item) => {
		const selectedChoiceIds =
			item.id != null
				? (itemStates?.get(item.id)?.selectedChoiceIds ?? [])
				: [];
		return isItemCorrect(item, selectedChoiceIds) ? count + 1 : count;
	}, 0);

	return {
		numCorrect,
		numTotal,
		percentage: numTotal ? Math.round((numCorrect / numTotal) * 100) : 0,
	};
};

const sumTrackedTimes = (times: Iterable<number | undefined>) => {
	let total = 0;
	let tracked = false;
	for (const time of times) {
		if (typeof time === "number") {
			total += time;
			tracked = true;
		}
	}
	return tracked ? total : undefined;
};

// The summative answer path records each item's posted time into `itemStates`,
// so a fresh finish sums those. The echoed `answer.timeTaken` only lands on a
// cold load, where it equals the same posted values - identical either way.
// Prefer the local sum and fall back to the echo; absent when neither carries a
// tracked time so the tiles degrade rather than report 0s.
export const sumProethicaTestTimeSeconds = (
	sessionItems: BaseItemSchema[],
	itemStates?: Map<number, ItemState>,
): number | undefined => {
	const localTotal = sumTrackedTimes(
		sessionItems.map((item) =>
			item.id == null ? undefined : itemStates?.get(item.id)?.timeTaken,
		),
	);
	if (localTotal != null) {
		return localTotal;
	}
	return sumTrackedTimes(sessionItems.map((item) => item.answer?.timeTaken));
};

const durationValue = (seconds?: number): ProethicaStatValue =>
	seconds == null ? { state: "absent" } : { state: "duration", seconds };

export const buildProethicaTestStatTiles = ({
	numTotal,
	totalTimeSeconds,
}: {
	numTotal: number;
	totalTimeSeconds?: number;
}): ProethicaStatTile[] => {
	const averageSeconds =
		totalTimeSeconds != null && numTotal > 0
			? Math.round(totalTimeSeconds / numTotal)
			: undefined;
	return [
		{
			key: "questions",
			icon: "help-circle-outline",
			value: { state: "count", count: numTotal },
		},
		{
			key: "timeTaken",
			icon: "clock-outline",
			value: durationValue(totalTimeSeconds),
		},
		{
			key: "avgPerQuestion",
			icon: "timer-outline",
			value: durationValue(averageSeconds),
		},
	];
};
